import { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Globe, Search, Plus, Building2, CheckCircle, ExternalLink, Navigation, Filter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dealers = () => {
  const [dealers, setDealers] = useState([]);
  const [filteredDealers, setFilteredDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [showListForm, setShowListForm] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [viewMode, setViewMode] = useState('map');
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const [listForm, setListForm] = useState({
    dealerName: '',
    contactPerson: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    province: '',
    description: '',
    specialties: []
  });

  const provinces = ['Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 'Free State', 'Mpumalanga', 'North West', 'Limpopo', 'Northern Cape'];
  const dealerTypes = ['All Dealers', 'WeBuyCars', 'Auto Pedigree', 'McCarthy', 'Imperial', 'Halfway', 'Toyota', 'BMW', 'Mercedes', 'Volkswagen', 'Ford', 'Hyundai', 'Audi', 'Kia', 'Nissan', 'Independent'];
  const specialtyOptions = ['Used Cars', 'New Cars', 'Bakkies & SUVs', 'Luxury Vehicles', 'Budget Cars', 'Electric Vehicles', 'Commercial Vehicles', 'Finance Available', 'Trade-ins Welcome'];

  useEffect(() => {
    document.title = 'Car Dealers South Africa | Find Dealerships Near You | AutoSearch SA';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Find car dealerships across South Africa. Browse WeBuyCars, Auto Pedigree, independent dealers & more. List your dealership for free exposure.');
    
    // Load Leaflet
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => fetchDealers();
      document.body.appendChild(script);
    } else {
      fetchDealers();
    }
  }, []);

  useEffect(() => {
    filterDealers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedProvince, selectedType, dealers]);

  useEffect(() => {
    if (filteredDealers.length > 0 && window.L && mapRef.current && viewMode === 'map') {
      setTimeout(initMap, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredDealers, viewMode]);

  const fetchDealers = async () => {
    try {
      const res = await axios.get(`${API}/dealers/directory`);
      setDealers(res.data.dealers || []);
      setFilteredDealers(res.data.dealers || []);
    } catch (e) {
      console.error('Failed to fetch dealers:', e);
    } finally {
      setLoading(false);
    }
  };

  const filterDealers = () => {
    let filtered = [...dealers];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.name?.toLowerCase().includes(term) ||
        d.city?.toLowerCase().includes(term) ||
        d.type?.toLowerCase().includes(term) ||
        d.province?.toLowerCase().includes(term)
      );
    }
    
    if (selectedProvince) {
      filtered = filtered.filter(d => d.province === selectedProvince);
    }
    
    if (selectedType && selectedType !== 'All Dealers') {
      filtered = filtered.filter(d => d.type === selectedType || d.name?.includes(selectedType));
    }
    
    setFilteredDealers(filtered);
  };

  const initMap = () => {
    if (!window.L || !mapRef.current) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = window.L.map(mapRef.current).setView([-28.4793, 24.6727], 5);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(mapInstanceRef.current);
    }

    const dealerIcon = window.L.divIcon({
      className: 'custom-dealer-marker',
      html: `<div style="background: #3b82f6; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
          <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"></path>
          <circle cx="7" cy="17" r="2"></circle>
          <circle cx="17" cy="17" r="2"></circle>
        </svg>
      </div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 28]
    });

    filteredDealers.forEach(dealer => {
      if (dealer.lat && dealer.lng) {
        const marker = window.L.marker([dealer.lat, dealer.lng], { icon: dealerIcon })
          .addTo(mapInstanceRef.current)
          .bindPopup(`
            <div style="min-width: 180px;">
              <h3 style="font-weight: bold; margin-bottom: 4px; font-size: 14px;">${dealer.name}</h3>
              <p style="color: #666; font-size: 11px; margin-bottom: 4px;">${dealer.province}</p>
              <span style="display: inline-block; padding: 2px 6px; background: #3b82f6; color: white; border-radius: 10px; font-size: 10px; margin-bottom: 6px;">${dealer.type}</span>
              ${dealer.phone ? `<p style="font-size: 11px;">📞 ${dealer.phone}</p>` : ''}
              ${dealer.website ? `<a href="${dealer.website}" target="_blank" rel="noopener noreferrer" style="display: block; font-size: 11px; color: #2563eb; margin-bottom: 6px;">🌐 Visit Website</a>` : ''}
              <a href="https://www.google.com/maps/dir/?api=1&destination=${dealer.lat},${dealer.lng}" 
                 target="_blank" 
                 style="display: inline-block; margin-top: 6px; padding: 4px 8px; background: #10b981; color: white; border-radius: 4px; text-decoration: none; font-size: 11px;">
                Get Directions
              </a>
            </div>
          `);
        markersRef.current.push(marker);
      }
    });

    if (filteredDealers.length > 0) {
      const validDealers = filteredDealers.filter(d => d.lat && d.lng);
      if (validDealers.length > 0) {
        const bounds = window.L.latLngBounds(validDealers.map(d => [d.lat, d.lng]));
        mapInstanceRef.current.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  };

  const focusDealer = (dealer) => {
    setSelectedDealer(dealer);
    if (mapInstanceRef.current && dealer.lat && dealer.lng) {
      mapInstanceRef.current.setView([dealer.lat, dealer.lng], 14);
      markersRef.current.forEach(m => {
        if (m.getLatLng().lat === dealer.lat && m.getLatLng().lng === dealer.lng) {
          m.openPopup();
        }
      });
    }
  };

  const handleListSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    try {
      await axios.post(`${API}/dealers/submit`, listForm);
      setFormSubmitted(true);
      setListForm({
        dealerName: '', contactPerson: '', email: '', phone: '', website: '',
        address: '', city: '', province: '', description: '', specialties: []
      });
    } catch (e) {
      console.error('Failed to submit dealer:', e);
      alert('Failed to submit. Please try again.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const toggleSpecialty = (specialty) => {
    setListForm(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const getTypeColor = (type) => {
    const colors = {
      'WeBuyCars': 'bg-orange-500',
      'Auto Pedigree': 'bg-blue-600',
      'McCarthy': 'bg-red-600',
      'Imperial': 'bg-purple-600',
      'Halfway': 'bg-green-600',
      'BMW': 'bg-blue-800',
      'Mercedes': 'bg-gray-800',
      'Toyota': 'bg-red-500',
      'Volkswagen': 'bg-blue-700',
      'Independent': 'bg-teal-600'
    };
    return colors[type] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-12">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-10 h-10 text-blue-200" />
                <h1 className="text-3xl md:text-4xl font-bold">Car Dealers Directory</h1>
              </div>
              <p className="text-blue-100">
                Find {dealers.length}+ dealerships across South Africa
              </p>
            </div>
            <button
              onClick={() => setShowListForm(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition"
            >
              <Plus className="w-5 h-5" />
              List Your Dealership FREE
            </button>
          </div>
        </div>
      </section>

      <div className="container py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{dealers.length}+</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Dealerships</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-green-600">9</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Provinces</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-purple-600">1000s</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Cars Available</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 text-center shadow-sm">
            <div className="text-2xl font-bold text-amber-600">FREE</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">To List</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search dealers by name, city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
              />
            </div>
            <select
              value={selectedProvince}
              onChange={(e) => setSelectedProvince(e.target.value)}
              className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Provinces</option>
              {provinces.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white"
            >
              {dealerTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
              <button
                onClick={() => setViewMode('map')}
                className={`px-4 py-3 ${viewMode === 'map' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                Map
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-3 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                List
              </button>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Showing {filteredDealers.length} dealerships
          </div>
        </div>

        {/* Map View */}
        {viewMode === 'map' && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Map */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  Dealer Locations
                </h2>
              </div>
              {loading ? (
                <div className="h-[500px] flex items-center justify-center">
                  <div className="spinner"></div>
                </div>
              ) : (
                <div ref={mapRef} className="h-[500px] w-full"></div>
              )}
            </div>

            {/* Dealer List Sidebar */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-bold text-gray-900 dark:text-white">Nearby Dealers</h2>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {filteredDealers.slice(0, 50).map((dealer, idx) => (
                  <div
                    key={idx}
                    onClick={() => focusDealer(dealer)}
                    className={`p-3 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition ${
                      selectedDealer === dealer ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{dealer.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{dealer.province}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs text-white rounded-full ${getTypeColor(dealer.type)}`}>
                      {dealer.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDealers.map((dealer, idx) => (
              <div 
                key={idx} 
                className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 hover:shadow-md transition ${dealer.website ? 'cursor-pointer hover:ring-2 hover:ring-blue-500' : ''}`}
                onClick={() => dealer.website && window.open(dealer.website, '_blank')}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-gray-900 dark:text-white">{dealer.name}</h3>
                  <span className={`px-2 py-0.5 text-xs text-white rounded-full ${getTypeColor(dealer.type)}`}>
                    {dealer.type}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-2">
                  <MapPin className="w-3 h-3" />
                  {dealer.city && `${dealer.city}, `}{dealer.province}
                </p>
                {dealer.phone && (
                  <a href={`tel:${dealer.phone}`} onClick={(e) => e.stopPropagation()} className="text-sm text-blue-600 hover:underline flex items-center gap-1 mb-1">
                    <Phone className="w-3 h-3" />{dealer.phone}
                  </a>
                )}
                {dealer.website && (
                  <p className="text-sm text-green-600 flex items-center gap-1 mb-1">
                    <Globe className="w-3 h-3" />Click to visit website
                  </p>
                )}
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${dealer.lat},${dealer.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                >
                  <Navigation className="w-4 h-4" />Get Directions
                </a>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 bg-gradient-to-r from-green-600 to-green-700 rounded-xl p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-2">Are You a Car Dealer?</h2>
          <p className="text-green-100 mb-4">List your dealership for FREE and reach thousands of car buyers</p>
          <button
            onClick={() => setShowListForm(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-green-50 transition"
          >
            <Plus className="w-5 h-5" />List Your Dealership Now
          </button>
        </div>
      </div>

      {/* List Modal */}
      {showListForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">List Your Dealership</h2>
              <button onClick={() => setShowListForm(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            {formSubmitted ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Thank You!</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">Your dealership has been submitted. We'll review and add it within 24-48 hours.</p>
                <button onClick={() => { setShowListForm(false); setFormSubmitted(false); }} className="px-6 py-2 bg-blue-600 text-white rounded-lg">Close</button>
              </div>
            ) : (
              <form onSubmit={handleListSubmit} className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dealership Name *</label>
                    <input type="text" required value={listForm.dealerName} onChange={(e) => setListForm({...listForm, dealerName: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="e.g., ABC Motors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Person *</label>
                    <input type="text" required value={listForm.contactPerson} onChange={(e) => setListForm({...listForm, contactPerson: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="Your name" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                    <input type="email" required value={listForm.email} onChange={(e) => setListForm({...listForm, email: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="email@dealer.co.za" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone *</label>
                    <input type="tel" required value={listForm.phone} onChange={(e) => setListForm({...listForm, phone: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="011 xxx xxxx" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
                  <input type="url" value={listForm.website} onChange={(e) => setListForm({...listForm, website: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="https://www.yoursite.co.za" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address *</label>
                  <input type="text" required value={listForm.address} onChange={(e) => setListForm({...listForm, address: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="123 Main Road" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City *</label>
                    <input type="text" required value={listForm.city} onChange={(e) => setListForm({...listForm, city: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white" placeholder="Johannesburg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Province *</label>
                    <select required value={listForm.province} onChange={(e) => setListForm({...listForm, province: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white">
                      <option value="">Select Province</option>
                      {provinces.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Specialties</label>
                  <div className="flex flex-wrap gap-2">
                    {specialtyOptions.map(s => (
                      <button key={s} type="button" onClick={() => toggleSpecialty(s)}
                        className={`px-3 py-1 rounded-full text-sm transition ${listForm.specialties.includes(s) ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={() => setShowListForm(false)} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg">Cancel</button>
                  <button type="submit" disabled={formSubmitting} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold disabled:opacity-50">
                    {formSubmitting ? 'Submitting...' : 'Submit Dealership'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dealers;
